import React, { useEffect, useState, useMemo } from 'react';
import { maturityDistributionRequestsAPI, custodianMaturityPaymentsAPI, formatCurrency, formatDate } from '../../services/api';
import { Loader, RefreshCw, Search, Calendar, DollarSign, Users, FileText, CheckCircle2, Loader2 } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { toNumberSafe } from '../../utils/moneyUtils';

const getNumericValue = (value) => {
  if (value == null) return 0;
  if (typeof value === 'number') return value;
  if (typeof value === 'string') {
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : 0;
  }
  if (typeof value === 'object') {
    if ('$numberDecimal' in value) {
      const parsed = Number(value.$numberDecimal);
      return Number.isFinite(parsed) ? parsed : 0;
    }
    if ('$numberDouble' in value) {
      const parsed = Number(value.$numberDouble);
      return Number.isFinite(parsed) ? parsed : 0;
    }
  }
  return 0;
};

const RequestStatusBadge = ({ status }) => {
  const normalized = status?.toLowerCase();
  const config = {
    pending: { text: 'Pending L1', className: 'bg-yellow-100 text-yellow-800' },
    pending_l2: { text: 'Pending L2', className: 'bg-orange-100 text-orange-800' },
    approved: { text: 'Approved', className: 'bg-green-100 text-green-800' },
    rejected: { text: 'Rejected', className: 'bg-red-100 text-red-800' }
  }[normalized] || { text: status, className: 'bg-gray-100 text-gray-800' };

  return (
    <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${config.className}`}>
      {config.text}
    </span>
  );
};

const CustodianMaturityManagement = () => {
  const { user } = useAuth();
  const currentUserId = user?._id || user?.id;
  const [requests, setRequests] = useState([]);
  const [payments, setPayments] = useState([]);
  const [loading, setLoading] = useState(false);
  const [paymentsLoading, setPaymentsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [statusFilter, setStatusFilter] = useState('all');
  const [approvingId, setApprovingId] = useState(null);
  const [approvingPaymentId, setApprovingPaymentId] = useState(null);
  const [approvingSubInvestorId, setApprovingSubInvestorId] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  // State to store editable payment amounts for each payment's sub-investors
  // Key: paymentId_investorId, Value: paymentAmount
  const [paymentAmounts, setPaymentAmounts] = useState({});

  const fetchRequests = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await maturityDistributionRequestsAPI.getMyRequests({ status: 'all' });
      const data = response.data?.data || response.data || [];
      setRequests(Array.isArray(data) ? data : []);
    } catch (err) {
      setError(err.response?.data?.message || err.message);
      setRequests([]);
    } finally {
      setLoading(false);
    }
  };

  const fetchPayments = async () => {
    try {
      setPaymentsLoading(true);
      const response = await custodianMaturityPaymentsAPI.getPayments();
      const data = response.data?.data || response.data || [];
      setPayments(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error('Error fetching custodian maturity payments:', err);
      setPayments([]);
    } finally {
      setPaymentsLoading(false);
    }
  };

  useEffect(() => {
    fetchRequests();
    fetchPayments();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleApprove = async (request) => {
    try {
      setApprovingId(request._id);
      if (request.status === 'pending') {
        await maturityDistributionRequestsAPI.approveRequestL1(request._id);
      } else if (request.status === 'pending_l2') {
        await maturityDistributionRequestsAPI.approveRequestL2(request._id);
      }
      // After approval, refresh both requests and payments
      await fetchRequests();
      await fetchPayments();
    } catch (err) {
      alert(err.response?.data?.message || err.message || 'Failed to approve request.');
    } finally {
      setApprovingId(null);
    }
  };

  const getUserIdValue = (value) => {
    if (!value) return null;
    if (typeof value === 'string') return value;
    return value._id || value.id || null;
  };

  const canApproveL2 = (request) => {
    if (request.status !== 'pending_l2') return true;
    const l1ApproverId = getUserIdValue(request.l1ApprovedBy);
    if (!l1ApproverId) return true;
    if (!currentUserId) return false;
    return l1ApproverId.toString() !== currentUserId.toString();
  };

  const getActionLabel = (request) =>
    request.status === 'pending_l2' ? 'Approve L2' : 'Approve';

  const handleApproveL1 = async (payment) => {
    try {
      setApprovingPaymentId(payment._id);
      // Update payment to pending_l2 status (L1 approval)
      await custodianMaturityPaymentsAPI.approvePaymentL1(payment._id);
      await fetchPayments();
    } catch (err) {
      alert(err.response?.data?.message || err.message || 'Failed to approve L1.');
    } finally {
      setApprovingPaymentId(null);
    }
  };

  const handleApproveL2 = async (payment) => {
    try {
      setApprovingPaymentId(payment._id);
      await custodianMaturityPaymentsAPI.approvePaymentL2(payment._id, { comments: '' });
      alert('Payment distribution approved successfully. Maturity payouts created for sub-investors.');
      await fetchPayments();
    } catch (err) {
      alert(err.response?.data?.message || err.message || 'Failed to approve L2.');
    } finally {
      setApprovingPaymentId(null);
    }
  };

  const handleApproveSubInvestorL1 = async (payment, investorId) => {
    try {
      setApprovingSubInvestorId(`${payment._id}_${investorId}`);
      await custodianMaturityPaymentsAPI.approveSubInvestorL1(payment._id, investorId, { comments: '' });
      alert('Sub-investor L1 approved successfully. Status changed to Pending L2.');
      await fetchPayments();
    } catch (err) {
      alert(err.response?.data?.message || err.message || 'Failed to approve L1.');
    } finally {
      setApprovingSubInvestorId(null);
    }
  };

  const handleApproveSubInvestorL2 = async (payment, investorId) => {
    try {
      setApprovingSubInvestorId(`${payment._id}_${investorId}`);
      await custodianMaturityPaymentsAPI.approveSubInvestorL2(payment._id, investorId, { comments: '' });
      alert('Sub-investor L2 approved successfully. Maturity payout created for this sub-investor.');
      await fetchPayments();
    } catch (err) {
      alert(err.response?.data?.message || err.message || 'Failed to approve L2.');
    } finally {
      setApprovingSubInvestorId(null);
    }
  };

  // Separate pending requests (need approval) from approved requests
  const pendingRequests = useMemo(() => {
    return requests.filter(
      (r) => r.status === 'pending' || r.status === 'pending_l2'
    );
  }, [requests]);

  // Get approved requests that have corresponding payments
  const approvedPayments = useMemo(() => {
    return payments.filter((p) => {
      // Find the corresponding request
      const request = requests.find((r) => r._id === p.requestId?._id || r._id === p.requestId);
      return request && request.status === 'approved';
    });
  }, [payments, requests]);

  const filteredPendingRequests = useMemo(() => {
    let filtered = pendingRequests;

    if (statusFilter !== 'all') {
      filtered = filtered.filter((r) => r.status === statusFilter);
    }

    if (searchTerm) {
      const keyword = searchTerm.toLowerCase();
      filtered = filtered.filter((r) => {
        const bondName = r.bondId?.bondName?.toLowerCase() || '';
        const isin = r.bondId?.isin?.toLowerCase() || '';
        return bondName.includes(keyword) || isin.includes(keyword);
      });
    }

    return filtered;
  }, [pendingRequests, statusFilter, searchTerm]);

  const filteredPayments = useMemo(() => {
    let filtered = approvedPayments;

    if (searchTerm) {
      const keyword = searchTerm.toLowerCase();
      filtered = filtered.filter((p) => {
        const bondName = p.bondId?.bondName?.toLowerCase() || '';
        const isin = p.bondId?.isin?.toLowerCase() || '';
        return bondName.includes(keyword) || isin.includes(keyword);
      });
    }

    return filtered;
  }, [approvedPayments, searchTerm]);

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="space-y-6">
          {/* Page Header */}
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Maturity Management</h1>
            <p className="text-secondary-600 mt-1">
              Manage maturity payments to sub-investors
            </p>
          </div>

          {/* Search and Filter */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
            <div className="card lg:col-span-2">
              <div className="card-body">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-dark-gray w-4 h-4" />
                  <input
                    type="text"
                    placeholder="Search by bond name or ISIN..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="form-input pl-10"
                  />
                </div>
              </div>
            </div>

            <div className="card">
              <div className="card-body">
                <select
                  value={statusFilter}
                  onChange={(e) => setStatusFilter(e.target.value)}
                  className="form-input"
                >
                  <option value="all">All Statuses</option>
                  <option value="pending">Pending L1</option>
                  <option value="pending_l2">Pending L2</option>
                  <option value="approved">Approved</option>
                  <option value="rejected">Rejected</option>
                </select>
              </div>
            </div>
          </div>

          {/* Pending Requests Table */}
          {filteredPendingRequests.length > 0 && (
            <div className="card">
              <div className="card-header">
                <div className="flex items-center justify-between">
                  <h3>Maturity Distribution Requests (Pending Approval)</h3>
                  <button
                    onClick={() => {
                      fetchRequests();
                      fetchPayments();
                    }}
                    disabled={loading || paymentsLoading}
                    className="btn-secondary btn-sm flex items-center gap-1"
                  >
                    <RefreshCw className={`w-4 h-4 ${loading || paymentsLoading ? 'animate-spin' : ''}`} />
                    Refresh
                  </button>
                </div>
              </div>

              <div className="card-body p-0">
                {loading ? (
                  <div className="py-12 text-center">
                    <Loader className="h-8 w-8 animate-spin mx-auto text-primary-600" />
                    <p className="mt-2 text-sm text-gray-600">Loading requests...</p>
                  </div>
                ) : error ? (
                  <div className="py-12 text-center">
                    <p className="text-red-600">{error}</p>
                    <button
                      onClick={() => {
                        fetchRequests();
                        fetchPayments();
                      }}
                      className="mt-4 btn-secondary btn-sm"
                    >
                      Retry
                    </button>
                  </div>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="table">
                      <thead className="table-header">
                        <tr>
                          <th className="table-header-cell">Bond</th>
                          <th className="table-header-cell">Maturity Date</th>
                          <th className="table-header-cell">Amount</th>
                          <th className="table-header-cell">Status</th>
                          <th className="table-header-cell">Actions</th>
                        </tr>
                      </thead>
                      <tbody className="bg-white divide-y divide-gray-200">
                        {filteredPendingRequests.map((request) => (
                          <tr key={request._id} className="hover:bg-gray-50">
                            <td className="px-6 py-4">
                              <div>
                                <div className="text-sm font-medium text-gray-900">
                                  {request.bondId?.bondName || 'Unknown Bond'}
                                </div>
                                <div className="text-xs text-gray-500">
                                  ISIN: {request.bondId?.isin || 'N/A'}
                                </div>
                              </div>
                            </td>
                            <td className="px-6 py-4 text-sm text-gray-600">
                              {formatDate(request.maturityDate)}
                            </td>
                            <td className="px-6 py-4 text-sm font-medium text-gray-900">
                              {formatCurrency(getNumericValue(request.amount))}
                            </td>
                            <td className="px-6 py-4">
                              <RequestStatusBadge status={request.status} />
                            </td>
                            <td className="px-6 py-4">
                              {request.status === 'rejected' ? (
                                <span className="text-xs text-red-600">Request rejected</span>
                              ) : (
                                <button
                                  onClick={() => handleApprove(request)}
                                  disabled={approvingId === request._id || (request.status === 'pending_l2' && !canApproveL2(request))}
                                  className="btn-primary btn-sm"
                                >
                                  {approvingId === request._id
                                    ? 'Approving...'
                                    : getActionLabel(request)}
                                </button>
                              )}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Approved Payments - Card View */}
          {filteredPayments.length > 0 && (
            <div className="space-y-6">
              <div className="flex items-center justify-between">
                <h2 className="text-xl font-semibold text-gray-900">Approved Maturity Payments</h2>
                <button
                  onClick={() => {
                    fetchRequests();
                    fetchPayments();
                  }}
                  disabled={loading || paymentsLoading}
                  className="btn-secondary btn-sm flex items-center gap-1"
                >
                  <RefreshCw className={`w-4 h-4 ${loading || paymentsLoading ? 'animate-spin' : ''}`} />
                  Refresh
                </button>
              </div>

              {paymentsLoading ? (
                <div className="py-12 text-center">
                  <Loader className="h-8 w-8 animate-spin mx-auto text-primary-600" />
                  <p className="mt-2 text-sm text-gray-600">Loading payments...</p>
                </div>
              ) : (
                filteredPayments.map((payment) => {
                  const bond = payment.bondId || {};
                  const maturityDate = payment.maturityDate || bond.maturityDate;
                  const totalAmount = getNumericValue(payment.amount);
                  const subInvestors = payment.subInvestorPayments || [];

                  return (
                    <div key={payment._id} className="bg-white rounded-lg border border-gray-200 shadow-sm">
                      {/* Bond Header */}
                      <div className="px-6 py-4 border-b border-gray-200">
                        <div className="flex items-center justify-between">
                          <div>
                            <div className="flex items-center space-x-2">
                              <h3 className="text-lg font-medium text-gray-900">{bond.bondName || 'Unnamed Bond'}</h3>
                              {bond.tokenDetails?.symbol && (
                                <span className="px-2 py-0.5 text-xs font-semibold bg-primary-100 text-primary-800 rounded">
                                  {bond.tokenDetails.symbol}
                                </span>
                              )}
                            </div>
                            <div className="mt-1 flex items-center space-x-4 text-sm text-gray-500">
                              <span>ISIN: {bond.isin || '—'}</span>
                              <span>•</span>
                              <span>Bond #: {bond.bondNumber || '—'}</span>
                              <span>•</span>
                              <span>{bond.productType || '—'}</span>
                            </div>
                          </div>
                          <div className="flex items-center space-x-3">
                            <button
                              className="btn-outline btn-sm flex items-center space-x-1"
                              onClick={() => {
                                // Details modal can be implemented later
                                console.log('View details for payment:', payment._id);
                              }}
                            >
                              <FileText className="w-3.5 h-3.5" />
                              <span>Details</span>
                            </button>
                          </div>
                        </div>
                      </div>

                      {/* Payment Summary */}
                      <div className="px-6 py-4 bg-gray-50 border-b border-gray-200">
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
                          <div>
                            <span className="text-gray-600">Maturity Date:</span>
                            <div className="font-medium flex items-center mt-1">
                              <Calendar className="w-4 h-4 mr-1 text-gray-400" />
                              {maturityDate ? formatDate(maturityDate) : '—'}
                            </div>
                          </div>
                          <div>
                            <span className="text-gray-600">Total Payment Amount:</span>
                            <div className="font-medium flex items-center mt-1">
                              <DollarSign className="w-4 h-4 mr-1 text-gray-400" />
                              {formatCurrency(totalAmount, payment.currency || bond.currency || 'MYR')}
                            </div>
                            <div className="text-xs text-gray-500 mt-1">Custodian payment amount</div>
                          </div>
                          <div>
                            <span className="text-gray-600">Sub-Investors:</span>
                            <div className="font-medium flex items-center mt-1">
                              <Users className="w-4 h-4 mr-1 text-gray-400" />
                              {subInvestors.length}
                            </div>
                            <div className="text-xs text-gray-500 mt-1">Total sub-investors</div>
                          </div>
                        </div>
                      </div>

                      {/* Sub-Investors Table */}
                      {subInvestors.length > 0 ? (
                        <div className="px-6 py-4">
                          <div className="mb-3 text-sm text-gray-600">
                            {subInvestors.length} sub-investor{subInvestors.length > 1 ? 's' : ''} found
                          </div>
                          <div className="overflow-x-auto">
                            <table className="table">
                              <thead className="table-header">
                                <tr>
                                  <th className="table-header-cell">Investor</th>
                                  <th className="table-header-cell">Email</th>
                                  <th className="table-header-cell">Payment Amount</th>
                                  <th className="table-header-cell">Status</th>
                                  <th className="table-header-cell">Actions</th>
                                </tr>
                              </thead>
                              <tbody className="bg-white divide-y divide-gray-200">
                                {subInvestors.map((subInv, index) => {
                                  const investorKey = `${payment._id}_${subInv.investorId || index}`;
                                  const currentAmount = paymentAmounts[investorKey] !== undefined 
                                    ? paymentAmounts[investorKey]
                                    : toNumberSafe(subInv.paymentAmount, 0);
                                  const subInvestorStatus = subInv.status || 'pending';
                                  const isApproving = approvingSubInvestorId === `${payment._id}_${subInv.investorId}`;
                                  
                                  return (
                                    <tr key={subInv.investorId || index} className="hover:bg-gray-50">
                                      <td className="px-6 py-4">
                                        <div>
                                          <div className="text-sm font-medium text-gray-900">
                                            {subInv.investorName || 'Unknown'}
                                          </div>
                                          <div className="text-xs text-gray-500 font-mono">ID: {subInv.investorId || '—'}</div>
                                        </div>
                                      </td>
                                      <td className="px-6 py-4 text-sm text-gray-600">
                                        {subInv.email || '—'}
                                      </td>
                                      <td className="px-6 py-4">
                                        <div className="flex items-center gap-2">
                                          <input
                                            type="number"
                                            step="0.01"
                                            min="0"
                                            value={currentAmount}
                                            onChange={(e) => {
                                              const newValue = parseFloat(e.target.value) || 0;
                                              setPaymentAmounts(prev => ({
                                                ...prev,
                                                [investorKey]: newValue
                                              }));
                                            }}
                                            className="w-32 px-2 py-1 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                                            disabled={subInvestorStatus === 'approved'}
                                          />
                                          <span className="text-xs text-gray-500">
                                            {payment.currency || bond.currency || 'MYR'}
                                          </span>
                                        </div>
                                      </td>
                                      <td className="px-6 py-4">
                                        <SubInvestorStatusBadge status={subInvestorStatus} />
                                      </td>
                                      <td className="px-6 py-4">
                                        <SubInvestorApprovalButton
                                          payment={payment}
                                          subInvestor={subInv}
                                          currentUserId={currentUserId}
                                          onApproveL1={handleApproveSubInvestorL1}
                                          onApproveL2={handleApproveSubInvestorL2}
                                          isApproving={isApproving}
                                        />
                                      </td>
                                    </tr>
                                  );
                                })}
                              </tbody>
                            </table>
                          </div>
                        </div>
                      ) : (
                        <div className="px-6 py-8 text-center text-gray-500">
                          <Users className="w-12 h-12 mx-auto mb-2 text-gray-400" />
                          <p>No sub-investors assigned yet</p>
                          <p className="text-xs mt-1">Sub-investors will be auto-populated from holdings when the payment is created</p>
                        </div>
                      )}
                    </div>
                  );
                })
              )}
            </div>
          )}

          {/* Empty State */}
          {filteredPendingRequests.length === 0 && filteredPayments.length === 0 && !loading && !paymentsLoading && (
            <div className="card">
              <div className="card-body text-center py-12">
                <p className="text-gray-500">No maturity payments found</p>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

// Sub-Investor Status Badge Component
const SubInvestorStatusBadge = ({ status }) => {
  const normalized = status?.toLowerCase();
  const config = {
    pending: { text: 'Pending', className: 'bg-yellow-100 text-yellow-800' },
    pending_l2: { text: 'Pending L2', className: 'bg-orange-100 text-orange-800' },
    approved: { text: 'Approved', className: 'bg-green-100 text-green-800' },
    rejected: { text: 'Rejected', className: 'bg-red-100 text-red-800' }
  }[normalized] || { text: status, className: 'bg-gray-100 text-gray-800' };

  return (
    <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${config.className}`}>
      {config.text}
    </span>
  );
};

// Sub-Investor Approval Button Component
const SubInvestorApprovalButton = ({ payment, subInvestor, currentUserId, onApproveL1, onApproveL2, isApproving }) => {
  const getUserIdValue = (value) => {
    if (!value) return null;
    if (typeof value === 'string') return value;
    return value._id || value.id || null;
  };

  const status = subInvestor.status || 'pending';
  const investorId = subInvestor.investorId;

  if (status === 'approved') {
    return <span className="text-xs text-gray-500">Approved</span>;
  }

  if (status === 'pending_l2') {
    const l1ApproverId = getUserIdValue(subInvestor.l1ApprovedBy);
    // L2 can only be approved by a different custodian (not the L1 approver)
    const canApproveL2 = l1ApproverId && currentUserId && l1ApproverId.toString() !== currentUserId.toString();
    
    if (canApproveL2) {
      return (
        <button
          onClick={() => onApproveL2(payment, investorId)}
          disabled={isApproving}
          className="btn-primary btn-sm flex items-center gap-1"
        >
          {isApproving ? (
            <>
              <Loader2 className="w-3.5 h-3.5 animate-spin" />
              Approving...
            </>
          ) : (
            <>
              <CheckCircle2 className="w-3.5 h-3.5" />
              Approve L2
            </>
          )}
        </button>
      );
    } else {
      return (
        <span className="text-xs text-gray-500">
          Waiting for L2 approval by another custodian account
        </span>
      );
    }
  }

  if (status === 'pending') {
    return (
      <button
        onClick={() => onApproveL1(payment, investorId)}
        disabled={isApproving}
        className="btn-primary btn-sm flex items-center gap-1"
      >
        {isApproving ? (
          <>
            <Loader2 className="w-3.5 h-3.5 animate-spin" />
            Approving...
          </>
        ) : (
          <>
            <CheckCircle2 className="w-3.5 h-3.5" />
            Approve
          </>
        )}
      </button>
    );
  }

  return null;
};

export default CustodianMaturityManagement;

